import {
  Alert,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { AlertTriangle, MoreVertical, Pencil, Send, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../common/components/Toast";
import { UserAvatar } from "../../common/components/UserAvatar";
import { useProject } from "../../common/contexts/ProjectContext";
import { createInvite } from "../api/invites";

function TeamTab({ project, currentUserRole, financials, onEdit, onRemove }) {
  const { t } = useTranslation("members");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reinviting, setReinviting] = useState(false);

  const isOwner = currentUserRole === "owner";
  const isParent = currentUserRole === "producer" || currentUserRole === "co_producer";

  // Non-owners only see themselves and their sub-members (people they invited)
  const { currentProjectUser } = useProject();
  const visibleUsers = isOwner
    ? project?.users
    : project?.users?.filter(
        (u) =>
          u.id === currentProjectUser?.id || u.parent === currentProjectUser?.id,
      );

  // Identify members with missing royalty setup
  const membersNeedingSetup = (visibleUsers || []).filter((u) => {
    if (u.role === "owner" || u.role === "viewer") return false;
    if (u.role === "co_producer") return !u.revenue_share_percentage;
    if (u.role === "producer") {
      const f = financials[u.user_details?.id];
      return f && f.has_expenses === false;
    }
    return false;
  });

  const needsSetup = (user) => {
    if (user.role === "co_producer") return !user.revenue_share_percentage;
    if (user.role === "producer") {
      const f = financials[user.user_details?.id];
      return f && f.has_expenses === false;
    }
    return false;
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleReinvite = async (user) => {
    handleMenuClose();
    setReinviting(true);
    try {
      const inviteData = {
        email: user.user_details?.email,
        role: user.role,
        asset_ids: user.accessible_assets?.map((a) => a.asset_id) || [],
        ...(user.role === "co_producer" &&
          user.revenue_share_percentage && {
            revenue_share_percentage: user.revenue_share_percentage,
          }),
      };
      const invite = await createInvite(inviteData);
      const inviteLink = `${window.location.origin}/invite/${invite.id}`;
      await navigator.clipboard.writeText(inviteLink).catch(() => {});
      toast.success(
        t("messages.reinviteSuccess", {
          name: user.user_details?.name || user.user_details?.email,
        }),
      );
    } catch {
      toast.error(t("messages.reinviteError"));
    } finally {
      setReinviting(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case "owner":
        return "primary";
      case "producer":
        return "secondary";
      case "co_producer":
        return "info";
      default:
        return "default";
    }
  };

  const getAssetTypeStyle = (type) => {
    switch (type) {
      case "series":
        return {
          backgroundColor: "#EDE9FE",
          color: "#7C3AED",
          borderColor: "#C4B5FD",
        };
      case "episode":
        return {
          backgroundColor: "#DBEAFE",
          color: "#2563EB",
          borderColor: "#93C5FD",
        };
      default:
        return {};
    }
  };

  const renderAssetChips = (user) => {
    if (user.role === "owner") {
      return (
        <Chip
          label={t("table.allAssets")}
          size="small"
          color="default"
          variant="outlined"
        />
      );
    }
    const assets = user.accessible_assets || [];
    if (assets.length === 0) {
      return (
        <Typography variant="bodySm" color="text.secondary">
          -
        </Typography>
      );
    }
    const visible = assets.slice(0, 3);
    const remaining = assets.length - 3;
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {visible.map((a) => (
          <Chip
            key={a.asset_id}
            label={a.asset__title}
            size="small"
            variant="outlined"
            sx={getAssetTypeStyle(a.asset__asset_type)}
          />
        ))}
        {remaining > 0 && (
          <Tooltip
            title={assets
              .slice(3)
              .map((a) => a.asset__title)
              .join(", ")}
          >
            <Chip label={`+${remaining}`} size="small" variant="outlined" />
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {(isOwner || isParent) && membersNeedingSetup.length > 0 && (
        <Alert
          severity="warning"
          icon={<AlertTriangle size={20} />}
          sx={{ mb: 2 }}
        >
          <Typography variant="bodySm">
            {t("royaltyWarning.banner", {
              count: membersNeedingSetup.length,
              names: membersNeedingSetup
                .map((u) => u.user_details?.name)
                .join(", "),
            })}
          </Typography>
        </Alert>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("table.member")}</TableCell>
              {isOwner && <TableCell>{t("table.email")}</TableCell>}
              <TableCell>{t("table.role")}</TableCell>
              <TableCell>{t("table.assetAccess")}</TableCell>
              {isOwner && (
                <TableCell align="right">{t("table.financials")}</TableCell>
              )}
              {isOwner && <TableCell></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleUsers?.map((user) => {
              const userFinancials = financials[user?.user_details?.id];
              return (
                <TableRow
                  key={user.id}
                  sx={{ "&:hover": { backgroundColor: "action.hover" } }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <UserAvatar
                        name={user?.user_details?.name}
                        image={user?.user_details?.avatar}
                        size={40}
                      />
                      <Typography variant="bodyMd" sx={{ fontWeight: 500 }}>
                        {user?.user_details?.name || t("table.unknownUser")}
                      </Typography>
                    </Box>
                  </TableCell>
                  {isOwner && (
                    <TableCell>
                      <Typography variant="bodySm" color="text.secondary">
                        {user?.user_details?.email}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Box>
                      <Chip
                        label={
                          user?.role
                            ? t(`roles.${user.role.toLowerCase()}`)
                            : t("roles.member")
                        }
                        color={getRoleColor(user?.role)}
                        size="small"
                        sx={{ fontWeight: 500, textTransform: "capitalize" }}
                      />
                      {user?.role === "co_producer" &&
                        user?.revenue_share_percentage && (
                          <Typography
                            variant="bodySm"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {t("table.revenueShare", {
                              percentage: user.revenue_share_percentage,
                            })}
                          </Typography>
                        )}
                      {(isOwner || isParent) && needsSetup(user) && (
                        <Chip
                          icon={<AlertTriangle size={12} />}
                          label={t("royaltyWarning.chip")}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                      {user?.parent_name && (
                        <Typography
                          variant="bodySm"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {t("table.invitedBy", { name: user.parent_name })}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{renderAssetChips(user)}</TableCell>
                  {isOwner && (
                    <TableCell align="right">
                      {user.role === "owner" ? (
                        <Typography variant="bodySm" color="text.secondary">
                          -
                        </Typography>
                      ) : userFinancials ? (
                        <Box>
                          <Typography variant="bodySm" sx={{ fontWeight: 500 }}>
                            ${userFinancials.total_earnings?.toFixed(2)}
                          </Typography>
                          <Typography
                            variant="bodySm"
                            sx={{
                              color:
                                userFinancials.remaining > 0
                                  ? "warning.main"
                                  : "success.main",
                            }}
                          >
                            {t("table.remaining")}: $
                            {userFinancials.remaining?.toFixed(2)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="bodySm" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {isOwner && (
                    <TableCell align="right">
                      <IconButton
                        onClick={(event) => handleMenuOpen(event, user)}
                        size="small"
                      >
                        <MoreVertical size={18} />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {isOwner && (
          <MenuItem
            onClick={() => {
              onEdit(selectedUser);
              handleMenuClose();
            }}
          >
            <Pencil size={18} style={{ marginRight: 8 }} />
            {t("actions.editAccess")}
          </MenuItem>
        )}
        {isOwner &&
          selectedUser?.role !== "owner" &&
          selectedUser?.user_details?.email && (
            <MenuItem
              onClick={() => handleReinvite(selectedUser)}
              disabled={reinviting}
            >
              <Send size={18} style={{ marginRight: 8 }} />
              {t("actions.reinvite")}
            </MenuItem>
          )}
        {isOwner && (
          <MenuItem
            onClick={() => {
              onRemove(selectedUser);
              handleMenuClose();
            }}
            sx={{ color: "error.main" }}
          >
            <Trash2 size={18} style={{ marginRight: 8 }} />
            {t("actions.removeFromProject")}
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}

export default TeamTab;
